import { CustomLink } from "@/components/links/nav-link"
import { useNavigationWithSearch } from "@/hooks/useNavigationWithSearch"
import type { PaginationProps } from "antd"
import { ConfigProvider, Empty, Pagination, Select, Spin } from "antd"
import { FC, useState } from "react"
import { CSVLink } from "react-csv"
import { useNavigate } from "react-router"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { useAuth } from "../../../../../../../components/auth"
import { useGetLinkListLazyQuery } from "../../../../../../../graphql"
import { DownloadIcon, EditDots, PlusIcon, SearchIcon } from "../../../../../../../icons"
import { formatDate, removeSelectDuplicates } from "../../../../../../../utils"
import "./index.less"

const List: FC<LinkListInterface> = ({
  list,
  page,
  loading,
  setPage,
  filters,
  pageSize,
  setFilters,
  totalItems,
  setLoading,
  setPageSize,
  listForExport,
  exportLoading,
  downloadBtnRef,
  createExportList,
  createQueryFilters,
}) => {
  const { permissions } = useAuth()
  const blockPermissions = permissions?.firstMenu
    ?.find(item => item.name == "Content")
    ?.blocks?.find((block: any) => block.name == "Assets")

  const { navigateWithSearch } = useNavigationWithSearch()

  const [getLinkTitles] = useGetLinkListLazyQuery()

  const [titles, setTitles] = useState<SelectItems>()

  const onChangePage: PaginationProps["onChange"] = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const onChangePageSize: PaginationProps["onShowSizeChange"] = (_, pageSize: number) => {
    setPageSize(pageSize)
  }

  const filteredOption = (input: string, option: { label: string; value: string; key?: number }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())

  const getItems = async () => {
    setLoading(true)

    const titlesData = await getLinkTitles({
      fetchPolicy: "network-only",
      errorPolicy: "all",
      variables: {
        sort: "title:desc",
        filter: createQueryFilters(),
        pagination: {
          pageSize: 1000,
        },
      },
    })

    setTitles(
      removeSelectDuplicates(
        titlesData.data?.appNewsPosts?.data
          ?.map((item: any) => ({
            value: item?.attributes?.title!,
            label: item?.attributes?.title!,
            key: item?.id!,
          }))
          .filter((item: any) => item.value)!
      )
    )

    setLoading(false)
  }

  const handleFilter = (data: string) => {
    setFilters({
      ...filters,
      selectedTitle: data,
    })
  }

  const createSelectElement = (
    type: string,
    name: string,
    query: () => void,
    loading: boolean,
    options: SelectItems
  ) => (
    <div
      className={`${type?.toLowerCase().replace(" ", "-")} ${
        type == "title" && filters?.selectedTitle !== "All" && "active"
      }`}
      children={
        <Select
          showSearch
          onDropdownVisibleChange={() => query()}
          value={name}
          options={options}
          optionFilterProp={"children"}
          defaultActiveFirstOption={true}
          filterOption={filteredOption as any}
          onChange={data => handleFilter(data)}
          notFoundContent={
            loading || !options ? (
              <div className={"no-data"} children={<Spin />} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={"no-data"} />
            )
          }
          suffixIcon={<SearchIcon />}
        />
      }
    />
  )

  const handleCreateNewClick = () => {
    navigateWithSearch("/account/content/assets/new-asset")
  }

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition
        key={JSON.stringify(filters) + String(page) + String(pageSize)}
        timeout={250}
        classNames={"fade"}
        appear={true}
        unmountOnExit
      >
        <div className={"main-list-wrapper link-list"}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  colorPrimary: "black",
                  colorPrimaryBorder: "black",
                  colorPrimaryHover: "black",
                  controlOutline: "#fff",
                },
                Select: {
                  colorText: "#677685",
                  colorPrimaryBorder: "#fff",
                  colorPrimaryHover: "#fff",
                  controlOutline: "#fff",
                  colorBgContainerDisabled: "#fff",
                },
              },
            }}
          >
            <div className={"list-wrapper"}>
              <div className={"items top"}>
                {createSelectElement("title", "TITLE", () => getItems(), loading, titles!)}
                <div children={"TYPE"} />
                <div children={"DATE"} />
                <div children={"EDIT"} className={"centered"} />
              </div>

              {list &&
                list.map((item: any, i: number) => (
                  <div className={"items"} key={`list-item-${i}`}>
                    <div children={<p>{item?.attributes?.title || "-"}</p>} />
                    <div children={<p>{item?.attributes?.more_section_type?.data?.attributes?.label || ""}</p>} />
                    <div children={<p>{formatDate(item?.attributes?.createdAt)}</p>} />
                    <div
                      className={`centered ${!blockPermissions?.edit && "disabled"}`}
                      children={<CustomLink to={!blockPermissions?.edit ? "#" : item.id} children={<EditDots />} />}
                    />
                  </div>
                ))}

              {!list && (
                <div className={"spinner-wrapper"}>
                  <Spin />
                </div>
              )}

              {list?.length == 0 && (
                <div className={"empty"}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>

            <div
              className={"navigation"}
              children={
                <Pagination
                  showQuickJumper
                  current={page}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={onChangePage}
                  onShowSizeChange={onChangePageSize}
                />
              }
            />

            {blockPermissions?.edit && (
              <button onClick={handleCreateNewClick} className={"create-new"}>
                New Asset
                <PlusIcon />
              </button>
            )}

            <button
              onClick={() => createExportList()}
              className={`download ${!blockPermissions?.edit && "single"}`}
              disabled={!list}
            >
              Download CSV
              {exportLoading ? <Spin /> : <DownloadIcon />}
            </button>

            <CSVLink data={listForExport || []} filename={"links"} ref={downloadBtnRef as any} />
          </ConfigProvider>
        </div>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default List
